<template>
  <div class="highlights-panel px-3">
    <!-- Header -->
    <v-sheet
      class="no-shrink py-3 d-flex align-center justify-space-between"
      elevation="0"
    >
      <v-list-item-title class="font-weight-bold"
        >{{ $t('unitStudy.highlights.title') }} ({{ highlightCount }})</v-list-item-title
      >

      <v-btn
        icon="mdi-close"
        variant="text"
        @click="$store.dispatch('AppData/highlightsOpen', false)"
      />
    </v-sheet>

    <v-divider class="no-shrink" />

    <!-- Search -->
    <v-sheet class="no-shrink pt-3" elevation="0">
      <v-text-field
        v-model="search"
        :append-inner-icon="search ? '' : 'mdi-magnify'"
        :label="$t('unitStudy.highlights.searchLabel')"
        single-line
        hide-details
        clearable
        variant="outlined"
        flat
        density="compact"
        :placeholder="$t('unitStudy.highlights.searchPlaceholder')"
        class="mb-3"
        color="#E8E9EC"
        style="min-width: 100%"
      />
    </v-sheet>

    <!-- Add above your list -->
    <v-row dense class="px-2">
      <v-col cols="12">
        <v-btn-toggle
          v-model="activeColors"
          multiple
          class="my-2"
          density="compact"
        >
          <v-btn
            v-for="color in availableColors"
            :key="color"
            :value="color"
            class="filter-dot-btn"
            size="x-small"
          >
            <div class="dot" :style="{ backgroundColor: color }">
              <v-icon
                v-if="activeColors.includes(color)"
                icon="mdi-check"
                size="12"
                class="check-icon"
              />
            </div>
          </v-btn>
        </v-btn-toggle>
        <v-switch
          v-model="groupByColor"
          :label="$t('unitStudy.highlights.groupByColor')"
          color="rgb(165, 216, 103)"
          class="mt-2"
          density="compact"
          inset
        />
      </v-col>
    </v-row>

    <v-divider class="no-shrink" />

    <!-- Highlights list (scrolls) -->
    <v-list class="scroll-area" lines="two" density="compact">
      <template v-if="!loading && filtered.length">
        <template v-for="(group, section) in groupedBySection" :key="section">
          <v-list-subheader class="font-weight-bold text-grey-darken-3">{{
            sectionLabel(section)
          }}</v-list-subheader>

          <!-- If groupByColor is true, each section has color groups -->
          <template v-if="groupByColor">
            <template v-for="(colorGroup, color) in group" :key="color">
              <v-list-subheader class="pl-6 d-flex align-center">
                <span
                  :style="{
                    backgroundColor: color,
                    width: '10px',
                    height: '10px',
                    borderRadius: '50%',
                    display: 'inline-block',
                    marginRight: '8px',
                  }"
                />
                {{ colorLabel(color) }}
              </v-list-subheader>

              <v-list-item v-for="h in colorGroup" :key="h.id" @click="jump(h)">
                <template #prepend>
                  <v-avatar size="20">
                    <v-icon :style="{ color: h.bg }">mdi-circle</v-icon>
                  </v-avatar>
                </template>
                <div class="d-flex flex-column overflow-hidden">
                  <span class="highlight-text" :title="previewText(h)">
                    {{ previewText(h) }}
                  </span>
                </div>
                <template #append>
                  <v-btn
                    size="small"
                    icon
                    variant="text"
                    @click.stop="removeOne(h)"
                    :aria-label="$t('unitStudy.highlights.deleteAriaLabel')"
                  >
                    <v-icon size="18">mdi-delete</v-icon>
                  </v-btn>
                </template>
              </v-list-item>
            </template>
          </template>

          <!-- If groupByColor is off, just flat list per section -->
          <template v-else>
            <v-list-item v-for="h in group" :key="h.id" @click="jump(h)">
              <template #prepend>
                <v-avatar size="20">
                  <v-icon :style="{ color: h.bg }">mdi-circle</v-icon>
                </v-avatar>
              </template>
              <div class="d-flex flex-column overflow-hidden">
                <span class="highlight-text" :title="previewText(h)">
                  {{ previewText(h) }}
                </span>
              </div>
              <template #append>
                <v-btn
                  size="small"
                  icon
                  variant="text"
                  @click.stop="removeOne(h)"
                  aria-label="Delete comment"
                >
                  <v-icon size="18">mdi-delete</v-icon>
                </v-btn>
              </template>
            </v-list-item>
          </template>
        </template>
      </template>

      <template v-else-if="!loading">
        <v-list-item>
          <v-list-item-title>{{ $t('unitStudy.highlights.emptyStateTitle') }}</v-list-item-title>
          <v-list-item-subtitle>
            {{ $t('unitStudy.highlights.emptyStateSubtitle') }}
          </v-list-item-subtitle>
        </v-list-item>
      </template>

      <template v-else>
        <v-skeleton-loader type="list-item-two-line" v-for="i in 6" :key="i" />
      </template>
    </v-list>
  </div>
</template>

<script>
  import { mapState, mapActions } from 'vuex'
  import {
    jumpToParagraph,
    unhighlight,
  } from '@/components/utilities/highlightDom'
  import db from '@/firebase/init'

  const SECTION_ORDER = [
    'other',
    'truth',
    'equip',
    'application',
    'mission',
    'supplication',
  ]

  export default {
    name: 'HighlightsPanel',
    props: {
      courseId: { type: String, required: true },
      unitId: { type: String, required: true },
      sectionId: { type: String, required: true },
    },
    data() {
      return {
        search: '',
        activeColors: [],
        groupByColor: false,
        defaultHex: '#fff176',
      }
    },
    computed: {
      ...mapState('Highlights', ['items', 'loading']),
      // if you'd rather show the filtered count in the header, use this instead:
      highlightCount() {
        return Array.isArray(this.filtered) ? this.filtered.length : 0
      },
      filtered() {
        let result = this.items

        if (this.activeColors.length) {
          result = result.filter((h) =>
            this.activeColors.includes(this.normalizeColor(h.bg))
          )
        }

        if (this.search) {
          const s = this.search.toLowerCase()
          result = result.filter((h) =>
            this.previewText(h).toLowerCase().includes(s)
          )
        }

        return result
      },
      groupedBySection() {
        const groups = {}

        if (this.groupByColor) {
          for (const h of this.filtered) {
            const section = this.resolveSection(h)
            const color = this.normalizeToHex(h.bg)
            if (!groups[section]) groups[section] = {}
            if (!groups[section][color]) groups[section][color] = []
            groups[section][color].push(h)
          }
        } else {
          for (const h of this.filtered) {
            const section = this.resolveSection(h)
            if (!groups[section]) groups[section] = []
            groups[section].push(h)
          }
        }

        // reorder into an object with correct section order
        const ordered = {}
        for (const key of SECTION_ORDER) {
          if (groups[key]) {
            ordered[key] = groups[key]
          }
        }

        return ordered
      },
      availableColors() {
        const all = this.items.map((i) => this.normalizeColor(i.bg))
        return [...new Set(all)].sort()
      },
    },
    watch: {
      courseId: 'restart',
      unitId: 'restart',
      sectionId: 'restart',
    },
    mounted() {
      this.ensureListening()
    },
    beforeUnmount() {
      this.stopListening()
    },
    methods: {
      ...mapActions('Highlights', [
        'startListening',
        'stopListening',
        'remove',
      ]),
      async removeOne(h) {
        unhighlight({ paragraphID: h.paragraphID, index: h.index })
        await this.persistParagraphHTML(h.docId, h.paragraphID)
      },
      // Returns a normalized CSS color string from canvas (rgb(...) or #hex)
      normalizeColor(color) {
        const ctx = document.createElement('canvas').getContext('2d')
        ctx.fillStyle = color
        return ctx.fillStyle // browser-normalized
      },

      // Always return lowercased #rrggbb
      normalizeToHex(color) {
        const c = this.normalizeColor(color)
        if (c.startsWith('#')) return c.toLowerCase()
        // c is "rgb(r, g, b)" – convert to hex
        const m = c.match(/^rgb\((\d+),\s*(\d+),\s*(\d+)\)$/i)
        if (!m) return c.toLowerCase()
        const toHex = (n) => ('0' + Number(n).toString(16)).slice(-2)
        return `#${toHex(m[1])}${toHex(m[2])}${toHex(m[3])}`.toLowerCase()
      },

      colorLabel(hex) {
        const map = {
          '#64b5f6': this.$t('unitStudy.highlights.colors.blue'),
          '#a5d867': this.$t('unitStudy.highlights.colors.green'),
          '#e57373': this.$t('unitStudy.highlights.colors.red'),
          '#ffb74d': this.$t('unitStudy.highlights.colors.orange'),
          '#ffeb3b': this.$t('unitStudy.highlights.colors.yellow'),
          '#9c27b0': this.$t('unitStudy.highlights.colors.purple'),
        }
        const k = hex.toLowerCase()
        return map[k] || hex // fallback to hex if unknown
      },
      sectionLabel(code) {
        const map = {
          other: this.$t('unitStudy.highlights.sections.inTheBible'),
          truth: this.$t('unitStudy.highlights.sections.truth'),
          equip: this.$t('unitStudy.highlights.sections.equipping'),
          application: this.$t('unitStudy.highlights.sections.accountability'),
          mission: this.$t('unitStudy.highlights.sections.mission'),
          supplication: this.$t('unitStudy.highlights.sections.supplication'),
        }
        return map[code] || code
      },
      async persistParagraphHTML(docId, paragraphID) {
        const el = document.getElementById(paragraphID)
        if (!el) return

        const sanitized = el.innerHTML
          .replace(
            /<div[^>]*class="comment-container"[^>]*>[\s\S]*?<\/div>/gi,
            ''
          )
          .replace(
            /<div[^>]*class="comment-icon-container"[^>]*>[\s\S]*?<\/div>/gi,
            ''
          )

        const hasAny = /<span[^>]*class="[^"]*highlighted[^"]*"/i.test(
          sanitized
        )
        const ref = db.collection('coursecontent').doc(docId)
        if (hasAny) {
          await ref.update({ paragraph: sanitized })
        } else {
          await ref.delete()
        }
      },
      resolveSection(h) {
        const pid = h?.paragraphID || ''
        if (!pid) return 'other'

        const match = pid.match(/-([a-z])bc-/i)
        if (!match) return 'other'

        const code = match[1].toLowerCase()
        switch (code) {
          case 't':
            return 'truth'
          case 'e':
            return 'equip'
          case 'a':
            return 'application' // accountability
          case 'm':
            return 'mission'
          case 's':
            return 'supplication'
          case 'i':
            return 'truth' // if you want "In the Bible" grouped under Truth
          default:
            return 'other'
        }
      },
      ensureListening() {
        this.startListening({
          courseID: this.courseId,
          unitID: this.unitId,
          sectionID: this.sectionId,
        })
      },
      restart() {
        this.ensureListening()
      },
      previewText(h) {
        if (h.excerpt) return h.excerpt
        const tmp = document.createElement('div')
        tmp.innerHTML = h.paragraph || ''
        const spans = tmp.querySelectorAll('.highlighted')
        const sel = spans[h.index]
        const text = (sel ? sel.textContent : tmp.textContent || '').trim()
        const MAX = 140
        return text.length > MAX ? text.slice(0, MAX) + '…' : text
      },
      jump(h) {
        if (!jumpToParagraph(h.paragraphID)) {
          this.$nextTick(() => jumpToParagraph(h.paragraphID))
        }
      },
    },
  }
</script>

<style scoped>
  /* Make the panel fill the drawer and stack vertically */
  .highlights-panel {
    display: flex;
    flex-direction: column;
  }

  /* two-line wrap + ellipsis */
  .highlight-text {
    font-size: 15px;
    display: -webkit-box; /* required for line-clamp */
    line-clamp: 2;
    -webkit-line-clamp: 2; /* clamp to two lines */
    -webkit-box-orient: vertical;
    overflow: hidden; /* hide overflow */
    white-space: normal; /* allow wrapping */
    line-height: 1.4;
  }

  /* (optional) non-webkit fallback: approximates two lines */
  @supports not (-webkit-line-clamp: 2) {
    .highlight-text {
      display: block;
      max-height: calc(1.4em * 2);
    }
  }

  /* Keep header/search/dividers from growing */
  .no-shrink {
    flex: 0 0 auto;
  }

  /* The list gets all remaining space and scrolls */
  .scroll-area {
    flex: 1 1 auto;
    min-height: 0; /* critical so flex item can actually shrink */
    overflow: auto; /* scroll inside the drawer */
  }

  /* Scoped override for inset switch track when active */
  .v-switch.v-switch--inset.v-input--dirty
    :deep(.v-selection-control--dirty .v-switch__track) {
    opacity: 1 !important;
  }

  .filter-dot-btn {
    padding: 4px !important;
    box-shadow: none;
    transition: 0.2s ease-in-out;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .dot {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .check-icon {
    color: white;
    position: absolute;
    top: 0;
    left: 0;
    transform: translate(-25%, -25%);
    background-color: rgba(0, 0, 0, 0.4);
    border-radius: 50%;
  }
</style>
